'use client';

import * as React from 'react';

import { cn } from '../lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type = 'text', leadingIcon, trailingIcon, 'aria-invalid': ariaInvalid, ...props },
    ref,
  ) => {
    const hasLeading = leadingIcon !== undefined;
    const hasTrailing = trailingIcon !== undefined;
    const invalid = ariaInvalid === true || ariaInvalid === 'true';

    const inputEl = (
      <input
        ref={ref}
        type={type}
        aria-invalid={ariaInvalid}
        className={cn(
          'border-input bg-card ring-offset-background placeholder:text-muted-foreground-soft focus-visible:ring-ring/60 hover:border-border-hover flex h-9.5 w-full rounded-xl border px-3.5 text-[13px] transition-[background-color,border-color,box-shadow] file:border-0 file:bg-transparent file:text-[13px] file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          hasLeading && 'pl-10',
          hasTrailing && 'pr-10',
          invalid && 'border-destructive focus-visible:ring-destructive',
          className,
        )}
        {...props}
      />
    );

    if (!hasLeading && !hasTrailing) {
      return inputEl;
    }

    return (
      <div className="relative w-full">
        {hasLeading ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 flex -translate-y-1/2 items-center [&_svg]:size-4"
          >
            {leadingIcon}
          </span>
        ) : null}
        {inputEl}
        {hasTrailing ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 flex -translate-y-1/2 items-center [&_svg]:size-4"
          >
            {trailingIcon}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
